/**
 * Defer spec commits during rapid typing.
 *
 * The tile-edit popover's title input previously called onSpecChange
 * on every keystroke, which re-cloned the whole spec tree and re-
 * rendered every tile in the dashboard. On a 20-tile board this is
 * a measurable hitch even on fast machines.
 *
 * This hook wraps the parent's onSpecChange with two safety layers:
 *
 * 1. **Debounce** — keystrokes-within-`waitMs` collapse into a single
 *    commit. The local input still updates immediately because the
 *    caller controls its `value` state directly; only the parent-tree
 *    ripple is delayed.
 *
 * 2. **startTransition** — wraps the eventual onSpecChange call so
 *    React schedules the re-render at "non-urgent" priority. If the
 *    user keeps typing before the cascade finishes, React aborts the
 *    in-flight render and starts the next one, instead of blocking
 *    keystrokes behind a render queue.
 *
 * Callers must call `commitNow()` before any action that depends on
 * the latest spec hitting the parent — close, move-up/down, switch
 * tiles, etc. — so a pending debounced commit doesn't get stranded.
 */

import { startTransition, useEffect, useMemo, useRef } from "react"

import type { DashboardSpec } from "../dashboard/catalog"

export interface DeferredSpecCommit {
  /** Schedule a commit; will fire after `waitMs` of no further calls. */
  scheduleCommit(next: DashboardSpec): void
  /**
   * Flush any pending commit immediately. Safe to call when nothing
   * is pending — no-op in that case.
   */
  commitNow(): void
  /** Drop a pending commit without firing it. Used on unmount. */
  cancel(): void
}

/**
 * Pure factory — no React. Used by the hook below and by unit tests.
 *
 * @param onSpecChange  Live setter; the factory invokes it inside
 *                      a `wrapper` if provided (the React hook passes
 *                      `startTransition` so updates run at non-urgent
 *                      priority).
 * @param wrapper       Optional outer wrapper applied around the
 *                      onSpecChange call. Tests omit it; production
 *                      uses `startTransition`.
 */
export function createDeferredCommit(
  onSpecChange: (spec: DashboardSpec) => void,
  waitMs: number,
  wrapper: (fn: () => void) => void = (fn) => fn()
): DeferredSpecCommit {
  let pending: DashboardSpec | null = null
  let timer: ReturnType<typeof setTimeout> | null = null

  const flush = () => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
    const next = pending
    if (next === null) return
    pending = null
    wrapper(() => onSpecChange(next))
  }

  return {
    scheduleCommit(next) {
      pending = next
      if (timer) clearTimeout(timer)
      timer = setTimeout(flush, waitMs)
    },
    commitNow() {
      flush()
    },
    cancel() {
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
      pending = null
    },
  }
}

/**
 * React hook variant. Same shape as `createDeferredCommit` but tied
 * to a component lifecycle: flushes pending on unmount, holds the
 * latest onSpecChange ref so debounced flushes always call the
 * freshest callback, and wraps each commit in startTransition.
 */
export function useDeferredSpecCommit(
  onSpecChange: (spec: DashboardSpec) => void,
  waitMs = 200
): DeferredSpecCommit {
  const callbackRef = useRef(onSpecChange)
  useEffect(() => {
    callbackRef.current = onSpecChange
  }, [onSpecChange])

  const handle = useMemo(
    () =>
      createDeferredCommit(
        // Indirect through the ref so a parent re-render with a new
        // callback identity doesn't strand the latest closure.
        (spec) => callbackRef.current(spec),
        waitMs,
        (fn) => startTransition(fn)
      ),
    [waitMs]
  )

  // Drop pending edits on unmount instead of flushing them.
  //
  // Flushing on unmount used to fire the parent's onSpecChange after
  // the parent might have already torn down — e.g. when a route
  // change unmounts the dashboard, the popover unmounts too and
  // commits a write into a stale closure that points at the prior
  // page's setter. Better behaviour: rely on explicit close-handlers
  // (handleTypeChange / handleMoveUp / handleMoveDown call
  // commitNow before their action; handleRemove calls cancel) and
  // discard anything still buffered when the component unmounts
  // for reasons we don't control.
  useEffect(() => {
    return () => {
      handle.cancel()
    }
  }, [handle])

  return handle
}
